import { useAuth } from "@/contexts/AuthContext";
import { useCasePhysician } from "@/hooks/useCasePhysician";
import { useCaseService } from "@/hooks/useCaseService";
import type { CreateCaseResponse } from "@/services/caseServiceV2.types";
import { getErrorMessage } from "@/utils/errorUtils";
import { isProfessional } from "@/utils/roleUtils";
import { useCallback } from "react";
import { toast } from "react-hot-toast";

export interface CreateCaseResult {
  case: CreateCaseResponse;
  physicianAssigned: boolean;
}

/**
 * Creates a case and, when the creator is a physician, assigns them to it.
 *
 * - Returns null when the case could not be created — callers navigate on a non-null result.
 * - physicianAssigned is false when the creator is not a physician, or when the
 *   physician self-assign call failed (the failure is toasted and swallowed because the
 *   case row already exists and must not be discarded).
 * - A physician gets plain text rather than a picker on the case detail page, so one of
 *   their cases with no assignment strands them on a permanently disabled Request Review.
 */
export const useCreateCase = () => {
  const { user, firmContext } = useAuth();
  const { assignPhysician } = useCasePhysician();
  const caseService = useCaseService();

  const createCase = useCallback(async (): Promise<CreateCaseResult | null> => {
    if (!user) {
      toast.error("You must be logged in to create a case");
      return null;
    }
    if (!firmContext?.firmId) {
      toast.error("Please select a firm before creating a case");
      return null;
    }

    let caseData: CreateCaseResponse;
    try {
      caseData = await caseService.createCase(firmContext.firmId);
    } catch (error) {
      console.error("Error creating new case:", error);
      toast.error(getErrorMessage(error));
      return null;
    }

    if (isProfessional(firmContext)) {
      try {
        await assignPhysician(caseData.case_id, user.id);
        return { case: caseData, physicianAssigned: true };
      } catch (error) {
        console.error("Error auto-assigning physician to new case:", error);
        toast.error(
          "Case created, but we could not assign you as its physician. Ask an admin to assign one before requesting a review."
        );
      }
    }

    return { case: caseData, physicianAssigned: false };
  }, [user, firmContext, caseService, assignPhysician]);

  return { createCase };
};
